import type { GuestsGroup } from './api/types';
import type { Occupancy } from './occupancy';

export interface GuestsSelection {
  adults: number;
  children: number;
  rooms: number;
  /** Exact per-room occupancy, including every child age. */
  occupancy?: Occupancy;
}

export const DEFAULT_GUESTS: GuestsSelection = { adults: 1, children: 0, rooms: 1 };

export function guestsFromOccupancy(occupancy: Occupancy): GuestsSelection {
  return {
    adults: occupancy.reduce((sum, room) => sum + room.adults, 0),
    children: occupancy.reduce((sum, room) => sum + room.children.length, 0),
    rooms: occupancy.length,
    occupancy,
  };
}

/**
 * Evenly distributes adults/children across rooms. The real ETG API
 * supports a custom distribution per room (different adults/children in
 * each entry of `guests[]`, per "Best Practices for APIv3" §3.5) — this
 * keeps the simple 3-field picker honest until a screen needs per-room
 * control. Child ages default to 10 since this picker only collects a
 * count, not ages; real ages must be collected before this hits the API.
 */
export function toGuestsGroups(selection: GuestsSelection): GuestsGroup[] {
  if (selection.occupancy?.length) {
    return selection.occupancy.map((room) => ({ adults: room.adults, childAges: [...room.children] }));
  }

  const groups: GuestsGroup[] = Array.from({ length: selection.rooms }, () => ({
    adults: 0,
    childAges: [],
  }));

  for (let i = 0; i < selection.adults; i++) {
    groups[i % selection.rooms].adults++;
  }
  for (let i = 0; i < selection.children; i++) {
    groups[i % selection.rooms].childAges.push(10);
  }
  for (const group of groups) {
    if (group.adults === 0) group.adults = 1;
  }

  return groups;
}

export function formatGuestsLabel(selection: GuestsSelection): string {
  const guestCount = selection.adults + selection.children;
  const guestWord = guestCount === 1 ? 'Guest' : 'Guests';
  const roomWord = selection.rooms === 1 ? 'Room' : 'Rooms';
  return `${guestCount} ${guestWord}, ${selection.rooms} ${roomWord}`;
}

/** Drops the room count in the single-room case — the common one — so the
 * label fits a half-width field on a phone instead of ellipsing. Multiple
 * rooms are always spelled out, since that's the detail worth seeing. */
export function formatGuestsLabelCompact(selection: GuestsSelection): string {
  const guestCount = selection.adults + selection.children;
  return `${guestCount}g · ${selection.rooms}r`;
}

export function toEtgGuests(guests: GuestsSelection): Occupancy {
  if (guests.occupancy?.length) return guests.occupancy;
  const rooms = Math.max(1, Math.min(3, guests.rooms));
  const adultsPerRoom = Math.max(1, Math.floor(guests.adults / rooms));
  return Array.from({ length: rooms }, (_, index) => ({
    adults: adultsPerRoom + (index < guests.adults % rooms ? 1 : 0),
    children: index === 0 ? Array.from({ length: guests.children }, () => 8) : [],
  }));
}
